import React, { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { subscriptionAPI } from '../services/api';
import { LoadingSpinner, Card } from './ui';
import { SparklesIcon } from '@heroicons/react/24/outline';
import type { PublicPlanDefinition, SubscriptionTier } from '../types/platformOrganization';
import { isHigherTier, TIER_ORDER } from '../utils/subscriptionTiers';
import { StripeCheckoutLink } from './StripeCheckoutLink';
import toast from 'react-hot-toast';

type SubscriptionSummary = {
  organizationId: string;
  organizationName: string;
  tier: string;
  checkoutUrls?: Partial<Record<SubscriptionTier, string | null>>;
  plan: {
    label: string;
    priceUsd: number;
    limits: Record<string, number | null>;
    features: Record<string, boolean>;
  };
  usage: {
    tenants: number;
    users: number;
    activeClients: number;
    servicesThisMonth: number;
  };
};

function formatLimit(value: number | null | undefined) {
  if (value == null) return '∞';
  return String(value);
}

function UsageRow({
  label,
  used,
  max,
}: {
  label: string;
  used: number;
  max: number | null | undefined;
}) {
  const unlimited = max == null;
  const pct = unlimited ? 0 : max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const nearLimit = !unlimited && max > 0 && used / max >= 0.85;

  return (
    <div>
      <div className="flex justify-between text-xs text-slate-600 mb-1">
        <span>{label}</span>
        <span className={nearLimit ? 'font-semibold text-amber-700' : ''}>
          {used} / {formatLimit(max)}
        </span>
      </div>
      {!unlimited && (
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${nearLimit ? 'bg-amber-500' : 'bg-slate-800'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function checkoutHref(
  summary: SubscriptionSummary,
  tier: SubscriptionTier,
  fallback?: string | null
) {
  return summary.checkoutUrls?.[tier] || fallback || null;
}

export const SubscriptionPlanCard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const sessionId = searchParams.get('session_id');

  const { data, isLoading, isError } = useQuery(['subscription-current'], () =>
    subscriptionAPI.getCurrent()
  );
  const { data: plansRes } = useQuery(['subscription-plans-public'], () =>
    subscriptionAPI.listPlans()
  );

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    (async () => {
      try {
        await subscriptionAPI.getCheckoutStatus(sessionId);
        if (cancelled) return;
        await queryClient.invalidateQueries(['subscription-current']);
        toast.success('Pago recibido. Su plan se actualizará en unos segundos.');
        const next = new URLSearchParams(searchParams);
        next.delete('session_id');
        setSearchParams(next, { replace: true });
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'No se pudo confirmar el pago. Si ya pagó, el plan se activará automáticamente.';
        toast.error(msg);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, queryClient, searchParams, setSearchParams]);

  if (isLoading) {
    return (
      <Card className="p-6 flex justify-center">
        <LoadingSpinner />
      </Card>
    );
  }

  if (isError || !data?.data?.data) {
    return null;
  }

  const summary = data.data.data as SubscriptionSummary;
  const { plan, usage } = summary;
  const publicPlans = (plansRes?.data?.data || []) as PublicPlanDefinition[];
  const upgradePlans = TIER_ORDER.map((tier) => publicPlans.find((p) => p.tier === tier))
    .filter((p): p is PublicPlanDefinition => Boolean(p && isHigherTier(p.tier, summary.tier)))
    .filter((p) => checkoutHref(summary, p.tier, p.stripeCheckoutUrl));

  const currentHref = checkoutHref(summary, summary.tier as SubscriptionTier);

  return (
    <Card className="p-6 border border-slate-200/90">
      <div className="flex items-start gap-3 mb-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200">
          <SparklesIcon className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Plan actual</p>
          <h3 className="text-lg font-bold text-slate-900">
            {plan.label}{' '}
            <span className="text-sm font-medium text-slate-500">— ${plan.priceUsd}/mes</span>
          </h3>
          <p className="text-sm text-slate-500">{summary.organizationName}</p>
        </div>
      </div>

      <div className="space-y-3">
        <UsageRow label="Funerarias" used={usage.tenants} max={plan.limits.maxTenants} />
        <UsageRow label="Usuarios" used={usage.users} max={plan.limits.maxUsers} />
        <UsageRow label="Clientes" used={usage.activeClients} max={plan.limits.maxActiveClients} />
        <UsageRow
          label="Servicios este mes"
          used={usage.servicesThisMonth}
          max={plan.limits.maxServicesPerMonth}
        />
      </div>

      {upgradePlans.length > 0 ? (
        <div className="mt-5 pt-5 border-t border-slate-200">
          <p className="text-sm font-semibold text-slate-800 mb-3">Mejorar plan</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {upgradePlans.map((p) => {
              const href = checkoutHref(summary, p.tier, p.stripeCheckoutUrl);
              if (!href) return null;
              return (
                <StripeCheckoutLink
                  key={p.tier}
                  href={href}
                  variant={p.tier === 'PROFESSIONAL' ? 'primary' : 'outline'}
                >
                  {p.label} — ${p.priceUsd}/mes
                </StripeCheckoutLink>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Pago seguro en Stripe. Al pagar desde aquí, su cuenta queda vinculada y el plan se activa
            automáticamente.
          </p>
        </div>
      ) : currentHref ? (
        <div className="mt-5 pt-5 border-t border-slate-200">
          <StripeCheckoutLink href={currentHref} className="w-full sm:w-auto">
            Renovar o gestionar pago — ${plan.priceUsd}/mes
          </StripeCheckoutLink>
        </div>
      ) : (
        <p className="mt-4 text-xs text-slate-500">
          Para ampliar límites o habilitar funciones adicionales, contacte a soporte.
        </p>
      )}
    </Card>
  );
};

export default SubscriptionPlanCard;
